import { IToothMovementLimits, MovementType, LimitProperty } from '@data/models';

export type MovementColorLevel = 'normal' | 'attention' | 'warning' | 'danger';

/**
 * Determines the color level for a movement value based on limits
 * Logic:
 * - From 0 to Tertiary (Medium) - normal color
 * - From Tertiary to Secondary (Hard) - orange (attention)  
 * - From Secondary to Primary (Technical) - red text (warning)
 * - Beyond Primary - red text with background (danger)
 * 
 * If limits are not enabled:
 * - If no Secondary and no Tertiary: only Primary limits apply
 * - If no Secondary: 0 to Tertiary is normal
 * - If no Tertiary: 0 to Secondary is normal
 */
export function getMovementColorLevel(
  value: number,
  limits: IToothMovementLimits,
  movementType: MovementType
): MovementColorLevel {
  const limitMappings = {
    [MovementType.MesialDistalTranslation]: {
      min: LimitProperty.MesialDistalTranslationMin,
      max: LimitProperty.MesialDistalTranslationMax
    },
    [MovementType.BuccalLingualTranslation]: {
      min: LimitProperty.BuccalLingualTranslationMin, 
      max: LimitProperty.BuccalLingualTranslationMax
    },
    [MovementType.ExtrusionIntrusionTranslation]: {
      min: LimitProperty.ExtrusionIntrusionMin,
      max: LimitProperty.ExtrusionIntrusionMax
    },
    [MovementType.BuccalLingualAngulation]: {
      min: LimitProperty.BuccalLingualTorqueMin,
      max: LimitProperty.BuccalLingualTorqueMax  
    },
    [MovementType.AxialRotation]: {
      min: LimitProperty.AxialRotationMin,
      max: LimitProperty.AxialRotationMax
    },
    [MovementType.MesialDistalAngulation]: {
      min: LimitProperty.MesialDistalAngulationMin,
      max: LimitProperty.MesialDistalAngulationMax
    }
  } as const;

  const mapping = limitMappings[movementType];
  if (!mapping) {
    return 'normal';
  }

  const primaryMin = limits.primaryLimits[mapping.min];
  const primaryMax = limits.primaryLimits[mapping.max];
  
  const secondaryMin = limits.secondaryLimits?.[mapping.min];
  const secondaryMax = limits.secondaryLimits?.[mapping.max];
  
  const tertiaryMin = limits.tertiaryLimits?.[mapping.min];
  const tertiaryMax = limits.tertiaryLimits?.[mapping.max];

  if (+value.toFixed(2) <= primaryMin || +value.toFixed(2) >= primaryMax) {
    return 'danger';
  }

  if (secondaryMin !== undefined && secondaryMax !== undefined &&
      (+value.toFixed(2) < secondaryMin || +value.toFixed(2) > secondaryMax)) {
    return 'warning';
  }

  if (tertiaryMin !== undefined && tertiaryMax !== undefined &&
      (+value.toFixed(2) < tertiaryMin || +value.toFixed(2) > tertiaryMax)) {
    return 'attention';
  }

  return 'normal';
}

const NORMAL_TEXT_CLASS = '';

export function getMovementValueColorClassName(
  value: number,
  limits: IToothMovementLimits | undefined,
  movementType: MovementType
): string {
  if (!limits) {
    return NORMAL_TEXT_CLASS;
  }
  
  const level = getMovementColorLevel(value, limits, movementType);
  return getMovementColorClassName(level);
}

export function getMovementColorClassName(level: MovementColorLevel): string {
  switch (level) {
    case 'normal':
      return NORMAL_TEXT_CLASS;
    case 'attention':
      return 'text-[rgba(255,137,3,0.75)]';
    case 'warning':
      return 'text-[rgba(175,78,74,1)]';
    case 'danger':
      return 'text-[rgba(175.78,74,1)] bg-[rgba(255,77,91,0.1)]';
    default:
      return NORMAL_TEXT_CLASS;
  }
}